import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const PlayerMetricsGameLog = ({ metrics, isPitcher }) => {
  const { games, trendOption } = useMemo(() => {
    const group = isPitcher ? 'pitching' : 'hitting';
    const splits = metrics?.lastGames?.stats?.find(s => s.group?.displayName === group)?.splits || [];
    const recent = splits.slice(0, 10).reverse();

    if (!recent.length) return { games: [], trendOption: null };

    const labels = recent.map((s, i) => {
      const d = s.date ? new Date(s.date) : null;
      return d ? `${d.getMonth() + 1}/${d.getDate()}` : `G${i + 1}`;
    });

    let option;
    if (!isPitcher) {
      option = {
        backgroundColor: 'transparent',
        grid: { top: 24, right: 20, bottom: 40, left: 44 },
        legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
        tooltip: { trigger: 'axis', backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
        xAxis: { type: 'category', data: labels, axisLabel: { color: '#a9c9b1', fontSize: 9, rotate: 30 }, axisLine: { lineStyle: { color: '#658d71' } } },
        yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#a9c9b1', fontSize: 9 }, axisLine: { lineStyle: { color: '#658d71' } }, splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } } },
        series: [
          { name: 'Hits', type: 'bar', data: recent.map(s => s.stat?.hits || 0), itemStyle: { color: 'rgba(169,201,177,0.85)', borderColor: '#a9c9b1', borderWidth: 1 }, stack: 'batting' },
          { name: 'HR', type: 'bar', data: recent.map(s => s.stat?.homeRuns || 0), itemStyle: { color: 'rgba(196,51,62,0.85)', borderColor: '#c4333e', borderWidth: 1 }, stack: 'batting' },
          { name: 'RBI', type: 'bar', data: recent.map(s => s.stat?.rbi || 0), itemStyle: { color: 'rgba(216,206,179,0.7)', borderColor: '#d8ceb3', borderWidth: 1 }, stack: 'batting' },
        ],
      };
    } else {
      option = {
        backgroundColor: 'transparent',
        grid: { top: 24, right: 20, bottom: 40, left: 44 },
        legend: { bottom: 0, textStyle: { color: '#a9c9b1', fontSize: 9 }, itemWidth: 10, itemHeight: 8 },
        tooltip: { trigger: 'axis', backgroundColor: '#1e3a29', borderColor: '#658d71', textStyle: { color: '#d8ceb3', fontSize: 10 } },
        xAxis: { type: 'category', data: labels, axisLabel: { color: '#a9c9b1', fontSize: 9, rotate: 30 }, axisLine: { lineStyle: { color: '#658d71' } } },
        yAxis: { type: 'value', minInterval: 1, axisLabel: { color: '#a9c9b1', fontSize: 9 }, axisLine: { lineStyle: { color: '#658d71' } }, splitLine: { lineStyle: { color: 'rgba(101,141,113,0.2)' } } },
        series: [
          { name: 'K', type: 'bar', data: recent.map(s => s.stat?.strikeOuts || 0), itemStyle: { color: 'rgba(169,201,177,0.85)', borderColor: '#a9c9b1', borderWidth: 1 } },
          { name: 'ER', type: 'bar', data: recent.map(s => s.stat?.earnedRuns || 0), itemStyle: { color: 'rgba(196,51,62,0.8)', borderColor: '#c4333e', borderWidth: 1 } },
          { name: 'BB', type: 'bar', data: recent.map(s => s.stat?.baseOnBalls || 0), itemStyle: { color: 'rgba(216,206,179,0.6)', borderColor: '#d8ceb3', borderWidth: 1 } },
        ],
      };
    }
    return { games: recent, trendOption: option };
  }, [metrics, isPitcher]);

  if (!games.length) return (
    <div className="text-[#658d71] text-xs text-center py-8 uppercase tracking-widest">No game log available</div>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Last {games.length} Games</span>
        </div>
        <div className="p-2"><ReactECharts option={trendOption} style={{ height: '180px' }} /></div>
      </div>

      <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] overflow-x-auto">
        <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
          <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Game Log</span>
        </div>
        <table className="w-full text-center" style={{ minWidth: '380px' }}>
          <thead>
            <tr className="border-b border-[#658d71] text-[#658d71] text-[8px] uppercase tracking-widest">
              <th className="px-2 py-1.5 text-left">Date</th>
              <th className="px-2 py-1.5 text-left">Opp</th>
              {!isPitcher
                ? ['AB', 'H', 'HR', 'RBI', 'R', 'BB', 'K'].map(h => <th key={h} className="px-1 py-1.5">{h}</th>)
                : ['IP', 'H', 'ER', 'BB', 'K', 'P'].map(h => <th key={h} className="px-1 py-1.5">{h}</th>)
              }
            </tr>
          </thead>
          <tbody>
            {games.map((g, i) => {
              const d = g.date ? new Date(g.date) : null;
              const dateStr = d ? `${d.getMonth() + 1}/${d.getDate()}` : '--';
              const opp = g.opponent?.abbreviation || '--';
              const s = g.stat || {};
              return (
                <tr key={i} className="border-b border-[rgba(101,141,113,0.2)] last:border-0 hover:bg-[rgba(255,255,255,0.03)]">
                  <td className="px-2 py-1.5 text-left text-[9px] text-[#658d71] font-bold">{dateStr}</td>
                  <td className="px-2 py-1.5 text-left text-[9px] text-[#a9c9b1] font-black uppercase">{opp}</td>
                  {!isPitcher
                    ? [s.atBats, s.hits, s.homeRuns || '–', s.rbi || '–', s.runs || '–', s.baseOnBalls || '–', s.strikeOuts || '–'].map((v, j) => (
                      <td key={j} className={`px-1 py-1.5 text-[10px] tabular-nums font-bold ${j === 2 && v !== '–' ? 'text-[#c4333e]' : 'text-[#a9c9b1]'}`}>{v ?? '--'}</td>
                    ))
                    : [s.inningsPitched, s.hits, s.earnedRuns, s.baseOnBalls, s.strikeOuts, s.pitchesThrown || '–'].map((v, j) => (
                      <td key={j} className={`px-1 py-1.5 text-[10px] tabular-nums font-bold ${j === 2 && v > 3 ? 'text-[#c4333e]' : 'text-[#a9c9b1]'}`}>{v ?? '--'}</td>
                    ))
                  }
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerMetricsGameLog;